import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { fetchUserNames } from "../../store/slices/userSlice";
import {
  fetchEmployeeDetails,
  updateEmployeeField,
} from "../../store/slices/employeeSlice";
import { fetchAttendanceMetadata } from "../../store/slices/attendanceSlice";
import EditableField from "./EditableField";
import { EmployeeDetails } from "../../models/employeeDetails.model";



const EmployeeInfo: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const users = useSelector((state: RootState) => state.users.allUsersNames);
  const employeeDetails = useSelector(
    (state: RootState) => state.employee.userDetails
  );
  const metadata = useSelector(
    (state: RootState) => state.attendance.attendanceMetadata
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<typeof users>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<EmployeeDetails>>({});

  useEffect(() => {
    dispatch(fetchUserNames());
  }, [dispatch]);

  useEffect(() => {
    if (selectedUserId) {
      dispatch(fetchEmployeeDetails(selectedUserId));
    }
  }, [dispatch, selectedUserId]);

  useEffect(() => {
    if (employeeDetails) {
      setFormData(employeeDetails);
    }
  }, [employeeDetails]);

  useEffect(() => {
    dispatch(fetchAttendanceMetadata());
  }, [dispatch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    let results = [...users];
    if (value.trim() !== "") {
      results = results.filter((user) =>
        user.username.toLowerCase().includes(value.toLowerCase())
      );
    }

    results.sort((a, b) => a.username.localeCompare(b.username));
    setFilteredUsers(results);
  };

  const handleUserSelect = (userId: string, username: string) => {
    if (selectedUserId) return;
    setSelectedUserId(userId);
    setSearchTerm(username);
    setFilteredUsers([]);
  };

  const handleClearSelection = () => {
    setSelectedUserId(null);
    setFormData({});
    setSearchTerm("");
    setFilteredUsers([]);
  };

const handleFieldUpdate = async (updatedField: Record<string, any>) => {
  if (!selectedUserId) return;

  const fieldKey = Object.keys(updatedField)[0];
  const value = updatedField[fieldKey];

  const updated = { ...formData, ...updatedField };
  const updatesToSend: Record<string, any> = { [fieldKey]: value };

  // If probationDays changes → update dateOfConfirmation
  if (fieldKey === "probationDays" && updated.dateOfJoining) {
    const doj = new Date(updated.dateOfJoining);
    doj.setDate(doj.getDate() + Number(value));
    const confirmationDate = doj.toISOString();

    if (formData.dateOfConfirmation?.slice(0, 10) !== confirmationDate.slice(0, 10)) {
      updatesToSend.dateOfConfirmation = confirmationDate;
    }
  }

  // If dateOfJoining changes → update dateOfConfirmation
  if (fieldKey === "dateOfJoining" && updated.probationDays) {
    const doj = new Date(value);
    doj.setDate(doj.getDate() + Number(updated.probationDays));
    const confirmationDate = doj.toISOString();

    if (formData.dateOfConfirmation?.slice(0, 10) !== confirmationDate.slice(0, 10)) {
      updatesToSend.dateOfConfirmation = confirmationDate;
    }
  }

  // If dateOfConfirmation changes → update probationDays (do NOT touch dateOfJoining)
  if (fieldKey === "dateOfConfirmation" && updated.dateOfJoining) {
    const doj = new Date(updated.dateOfJoining);
    const doc = new Date(value);
    const diffInDays = Math.round(
      (doc.getTime() - doj.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (formData.probationDays !== diffInDays) {
      updatesToSend.probationDays = diffInDays;
    }
  }

  setFormData((prev) => ({ ...prev, ...updatesToSend }));

  for (const [key, val] of Object.entries(updatesToSend)) {
    let valToSend = val;

    if (["dateOfJoining", "dateOfConfirmation", "pfJoiningDate", "dob", "marriageDate"].includes(key)) {
      valToSend = new Date(val).toISOString();
    }

    await dispatch(
      updateEmployeeField({
        userId: selectedUserId,
        fieldKey: key,
        value: valToSend,
      })
    );
  }
};


  const fieldGroups = [
    {
      title: "Personal Information",
      fields: [
        { label: "Employee Number", key: "employeeNo" },
        { label: "Surname", key: "surname" },
        { label: "Name", key: "aadharName" },
        { label: "Father's Name", key: "fatherName" },
        { label: "Spouse Name", key: "spouseName" },
        { label: "Date of Birth", key: "dob", type: "date" },
        { label: "Email", key: "email" },
        { label: "Mobile Number", key: "mobile" },
        {
          label: "Gender",
          key: "gender",
          optionsKey: "gender",
          type: "select",
        },
        {
          label: "Blood Group",
          key: "bloodGroup",
          optionsKey: "bloodGroup",
          type: "select",
        },
        {
          label: "Marital Status",
          key: "maritalStatus",
          optionsKey: "maritalStatus",
          type: "select",
        },
        { label: "Marriage Date", key: "marriageDate", type: "date" },
        { label: "Current Address", key: "currentAddress" },
        { label: "Permanent Address", key: "permanentAddress" },
      ],
    },
    // {
    //   title: "Identity Documents",
    //   fields: [

    //   ],
    // },
    {
      title: "Emergency Contact",
      fields: [
        { label: "Contact Name", key: "emergencyContactName" },
        { label: "Contact Number", key: "emergencyContactNumber" },
        { label: "Relationship", key: "emergencyContactRelation" },
      ],
    },
    {
      title: "Employment Details",
      fields: [
        {
          label: "Designation",
          key: "designation",
          optionsKey: "designations",
          type: "select",
        },
        {
          label: "Location",
          key: "location",
          optionsKey: "locations",
          type: "select",
        },
        {
          label: "Department",
          key: "department",
          optionsKey: "departments",
          type: "select",
        },
        {
          label: "Weekend Policy",
          key: "weekendPolicy",
          optionsKey: "weekendPolicies",
          type: "select",
        },
        { label: "Date Of Joining", key: "dateOfJoining", type: "date" },
        {
          label: "Employment Status",
          key: "employmentStatus",
          optionsKey: "employmentStatuses",
          type: "select",
        },
        {
          label: "Probation Period (Days)",
          key: "probationDays",
        },
        {
          label: "Date of Confirmation",
          key: "dateOfConfirmation",
          type: "date",
        },
        { label: "Previous Company Name", key: "previousCompanyName" },
        {
          label: "Previous Company Designation",
          key: "previousCompanyDesignation",
        },
        {
          label: "Previous Company Experience",
          key: "previousCompanyExperience",
        },
      ],
    },
    {
      title: "Bank & Financial",
      fields: [
        {
          label: "Payment Type",
          key: "paymentType",
          optionsKey: "paymentTypes",
          type: "select",
        },
        {
          label: "Bank Name",
          key: "bankName",
          optionsKey: "banks",
          type: "select",
        },
        { label: "Bank Branch", key: "branch" },
        { label: "Bank Account Number", key: "accountNo" },
        { label: "IFSC Code", key: "ifsc" },
        { label: "Account Holder Name", key: "accountHolderName" },
        { label: "Aadhaar Number", key: "aadhaar" },
        { label: "PAN Number", key: "pan" },
      ],
    },
    {
      title: "PF and ESIC",
      fields: [
        { label: "UAN Number", key: "uanNumber" },
        { label: "PF Number", key: "pfNumber" },
        { label: "ESI Number", key: "esiNumber" },
        { label: "PF Joining Date", key: "pfJoiningDate", type: "date" },
        {
          label: "Is Existing Member of EPS",
          key: "isExistingMemberOfEPS",
          type: "checkbox",
        },
        {
          label: "Allow EPF excess contribution",
          key: "allowEPFExcessContribution",
          type: "checkbox",
        },
        {
          label: "Allow EPS excess contribution",
          key: "allowEPSExcessContribution",
          type: "checkbox",
        },
      ],
    },
  ];

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-md p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-200">
          Employee Information Management
        </h2>

      {/* Search Bar */}
      <div className="mb-8">
        <div className="relative max-w-xs">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => {
              if (!selectedUserId) {
                const sortedUsers = [...users].sort((a, b) =>
                  a.username.localeCompare(b.username)
                );
                setFilteredUsers(sortedUsers);
              }
            }}
            placeholder="Search employees by name..."
            disabled={!!selectedUserId}
            className={`w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none ${
              selectedUserId
                ? "bg-gray-100 cursor-not-allowed"
                : "focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            }`}
          />
          {selectedUserId ? (
            <button
              onClick={handleClearSelection}
              className="absolute right-3 top-3.5 text-gray-400 hover:text-red-500 text-lg"
            >
              &#10005;
            </button>
          ) : (
            <svg
              className="absolute right-3 top-3.5 h-5 w-5 text-gray-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
          )}

          {filteredUsers.length > 0 && !selectedUserId && (
            <ul className="absolute z-10 mt-1 w-full border border-gray-200 rounded-lg shadow-lg bg-white max-h-60 overflow-y-auto">
              {filteredUsers.map((user) => (
                <li
                  key={user._id}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center border-b border-gray-100 last:border-0"
                  onClick={() => handleUserSelect(user._id, user.username)}
                >
                  <div className="bg-gray-200 border-2 border-dashed rounded-xl w-8 h-8 mr-3" />
                  <span className="font-medium">{user.username}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Employee Details */}
      {selectedUserId && (
        <div className="space-y-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              Employee Profile
            </h2>
            <div className="flex items-center">
              <div className="bg-gray-200 border-2 border-dashed rounded-xl w-12 h-12" />
              <div className="ml-3">
                <h3 className="font-medium">{searchTerm}</h3>
                <p className="text-sm text-gray-500">ID: {selectedUserId}</p>
              </div>
            </div>
          </div>

          {fieldGroups.map((group, index) => (
            <div key={index} className="mb-10">
              <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-200">
                {group.title}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {group.fields.map((field) => (
                  <EditableField
                    key={field.key}
                    label={field.label}
                    fieldKey={field.key}
                    value={
                      field.type === "date"
                        ? ((formData as any)[field.key] || "").slice(0, 10)
                        : (formData as any)[field.key] || ""
                    }
                    onUpdate={handleFieldUpdate}
                    metadata={metadata ?? undefined}
                    optionsKey={field.optionsKey}
                    type={
                      field.type as
                        | "text"
                        | "date"
                        | "select"
                        | "checkbox"
                        | undefined
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmployeeInfo;
